import { Action, applyMiddleware, getDefaultMiddleware, createStore, compose, StoreEnhancer } from "@reduxjs/toolkit";
import { ThunkAction } from "redux-thunk";
import { InteropStore, LinkerStore, ResolverStore, RootStore, WorkspaceStore } from "./types";
import { install as installReduxLoop } from "redux-loop";
import root from "./modules/root";
import resolver from "./modules/resolver";
import linker from "./modules/linker";
import workspace from "./modules/workspace";
import { createReducer, remoteReduxServerEnhancer, CreateReducerHook } from "../../common/redux";
import devtoolsEnhancer from "./devtoolsEnhancer";
import Logger from "../../clients/logger";

const linkerInitialState: LinkerStore = linker.getInitialState();
const resolverInitialState: ResolverStore = resolver.getInitialState();
const rootInitialState: RootStore = root.getInitialState();
const workspaceInitialState: WorkspaceStore = workspace.getInitialState();

/**
 * Each module should export its bit of initial state. Note that we're not using redux slices because
 * those prevent state changes across slices of state. Our modules are allowed to cross state, though it should still
 * be avoided if at all possible because of the risk of race conditions.
 */
export const initialState: InteropStore = {
	root: rootInitialState,
	linker: linkerInitialState,
	resolver: resolverInitialState,
	workspace: workspaceInitialState,
};

const loggerHook: CreateReducerHook = (previousState, nextState, action) => {
	// if heartbeat action then don't log the previous state to reduce overhead
	action.type.includes("/heartbeat")
		? Logger.system.debug("forceObjectsToLogger", `Interop.${action.type}`, action)
		: Logger.system.debug("forceObjectsToLogger", `Interop.${action.type}`, previousState, action);
};

/**
 * This is our proprietaty createReducer() derived from RTK's version. See createReducer.ts for docs.
 */
export const reducer = createReducer(
	initialState,
	(builder) => {
		root.addReducerCases(builder);
		resolver.addReducerCases(builder);
		linker.addReducerCases(builder);
		workspace.addReducerCases(builder);
	},
	loggerHook
);

/**
 * Gotchas from react-devtools
 *
 * Your reducers have to be pure and free of side effects to work correctly with DevTools.
 * For example, even generating a random ID in reducer makes it impure and non-deterministic.
 * Instead, do this in action creators.
 * Make sure to only apply DevTools.instrument() and render <DevTools> in development!
 * In production, this will be terribly slow because actions just accumulate forever.
 * As described above, you need to use conditional requires and use DefinePlugin (Webpack) or
 * loose-envify (Browserify) together with Uglify to remove the dead code. Here is an example
 * that adds Redux DevTools handling the production case correctly.
 * It is important that DevTools.instrument() store enhancer should be added to your middleware
 * stack after applyMiddleware in the composed functions, as applyMiddleware is potentially asynchronous.
 * Otherwise, DevTools won’t see the raw actions emitted by asynchronous middleware such as
 * redux-promise or redux-thunk.
 */

/**
 * Our proprietary createReducer() requires that redux-loops middleware be installed.
 *
 * Even though we're using our own version of RTK's createReducer we still use RTK's middleware because
 * it provides some nice runtime invariants that will print console errors when bad things happen in the store.
 *
 * The ordering here is critical. The remoteReduxServerEnhancer must be a parent of installReduxLoop
 * (it must come first in the compose order, which means it's the last item to act)
 */
const enhancer = compose(
	remoteReduxServerEnhancer({ server: "@@finsemble/remoteRedux/InteropStore" }),
	installReduxLoop(),
	applyMiddleware(...getDefaultMiddleware()),
	devtoolsEnhancer as StoreEnhancer
);

const store = createStore(reducer, initialState, enhancer);

/**
 * Be sure to initialize *after* creating the store. Here, a module may dispatch any actions (or AsyncThunks) that are
 * necessary before starting to run.
 * e.g. Loading state from config.
 */
export const initializeModules = () => {
	root.initialize(store);
	resolver.initialize(store);
	linker.initialize(store);
	workspace.initialize(store);
};

/**
 * Boilerplate type stuff, which is mostly not used. Just in case we need it someday...
 */
export type RootState = ReturnType<typeof reducer>;

export type AppDispatch = typeof store.dispatch;

export type AppThunk = ThunkAction<void, RootState, unknown, Action<string>>;

export type StoreType = typeof store;

export default store;
